import { createSlice } from "@reduxjs/toolkit";
import * as PRICING from "./../constants/Pricing";
import { loadStripe } from "@stripe/stripe-js";
import { createStripeCheckoutSession } from "./StripeActions";
import { notifyActions, NOTIFICATION_TYPE } from "./notification-slice";
import { consentReasonOf, gymTermsRefusalOf } from "../utils/commitmentConsent";

/* A free line contributes nothing to the total, in either direction.
 *
 * Deliberately unguarded on `pricing`: a line without it is a caller bug, and
 * this throws on the spot exactly as the cart always has. Reading a missing
 * price as 0 would let a broken payload settle quietly into the cart and be
 * discovered at checkout, as a total that does not match what is in it. */
const chargeableAmount = (item) =>
  item.pricing.type !== PRICING.TYPE.free ? item.pricing.amount : 0;

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    processingCheckout: false,
    showCheckout: false,
    cartItems: [],
    totalPrice: Number((0.0).toFixed(2)),
  },
  reducers: {
    showCheckout(state, actions) {
      state.showCheckout = actions.payload;
    },
    processingCheckout(state, actions) {
      state.processingCheckout = actions.payload;
    },
    /*
     * Upsert by programUID: a product occupies exactly one cart line, and
     * adding it again REPLACES that line rather than being ignored.
     *
     * Re-picking is the normal way to change your mind — reopen the plan
     * popover, choose a different length or a different way of paying, add.
     * Dropping the second add silently left the shopper looking at a success
     * toast while the cart still held the plan they had just rejected, and
     * they would have been charged for it.
     */
    addToCart(state, actions) {
      const incoming = actions.payload;
      const idx = state.cartItems.findIndex((item) => item.programUID === incoming.programUID);

      if (idx === -1) {
        state.cartItems.push(incoming);
        state.totalPrice = Number((state.totalPrice + chargeableAmount(incoming)).toFixed(2));
        return;
      }

      /* Move the total by the DELTA so it stays correct across re-picks,
       * including a swap between a free and a paid option in either direction. */
      const previous = state.cartItems[idx];
      state.totalPrice = Number(
        (state.totalPrice - chargeableAmount(previous) + chargeableAmount(incoming)).toFixed(2)
      );
      state.cartItems[idx] = incoming;
    },
    removeItem(state, actions) {
      /* deduct total amount */
      const idx = state.cartItems.findIndex((item) => item.programUID === actions.payload);
      state.totalPrice = Number(
        (state.totalPrice - state.cartItems[idx].pricing.amount).toFixed(2)
      );

      /* remove item */
      const newCartItems = state.cartItems.filter((item) => item.programUID !== actions.payload);
      state.cartItems = newCartItems;
    },
    resetCart(state) {
      state.processingCheckout = false;
      state.showCheckout = false;
      state.cartItems = [];
      state.totalPrice = 0.0;
    },
  },
});

export const cartActions = cartSlice.actions;
export default cartSlice;

export const MIXED_CART_BLOCK_MESSAGE =
  "Subscriptions and visit cards must be purchased separately.";

/*
 * A Stripe Checkout Session has a single mode: "subscription" or "payment".
 * A cart that mixes a recurring subscription with a one-time item (e.g. a visit
 * card) cannot be expressed in one session — Stripe rejects a subscription-mode
 * session that contains a one_time price, failing the whole checkout. Detect the
 * mix up front so we can block it with a clear message instead of surfacing
 * Stripe's raw error.
 */
export const hasMixedSubscriptionAndOneTime = (cartItems = []) => {
  let hasSubscription = false;
  let hasOneTime = false;
  for (const item of cartItems) {
    const type = item?.pricing?.type;
    if (type === PRICING.TYPE.subscription) {
      hasSubscription = true;
    } else if (type === PRICING.TYPE.oneTime) {
      hasOneTime = true;
    }
    if (hasSubscription && hasOneTime) {
      return true;
    }
  }
  return false;
};

/*
 * Start a checkout.
 *
 * `commitmentConsent` is the buyer's proof that they were shown the consumer-
 * rights wording and agreed to it, and the server REFUSES any cart the gate
 * covers without a valid one. It stays optional because the caller cannot know
 * the gate's scope: the server classifies the cart itself and returns early for
 * anything outside it.
 *
 * `gymTermsConsent` is its sibling for the gym's own terms, optional for the
 * same reason.
 *
 * Resolves to `{ ok }`, plus a `consentReason` when the refusal was about
 * consent specifically, and the whole `gymTermsRefusal` payload when it was
 * about the gym's terms. A caller with its own consent-recovery UI declares
 * that by passing `handlesConsentRefusals: true` — for it, consent refusals
 * are reported through the return value rather than announced (the generic
 * banner cannot re-open a checkbox or re-fetch the terms, and it would talk
 * over the screen that can). Every other caller, and every other failure,
 * keeps the notification path it has always had. Declaring it explicitly —
 * rather than inferring it from whether a consent payload was sent — matters
 * because the recovery-capable caller legitimately sends NO payload for a
 * cart it did not classify as committing, and a refusal on exactly that cart
 * is the case its recovery UI most needs to own alone.
 */
export const checkout = (
  cartItems,
  commitmentConsent = null,
  gymTermsConsent = null,
  { handlesConsentRefusals = false } = {}
) => {
  return async (dispatch) => {
    /* Block a mixed subscription + one-time (visit card) cart before touching
     * Stripe — these require separate checkout sessions (see
     * hasMixedSubscriptionAndOneTime). */
    if (hasMixedSubscriptionAndOneTime(cartItems)) {
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.error,
          title: "Checkout ERROR",
          message: MIXED_CART_BLOCK_MESSAGE,
        })
      );
      return { ok: false, consentReason: null, gymTermsRefusal: null };
    }

    dispatch(cartActions.processingCheckout(true));

    /* Creating Product on Stripe */
    const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISH_KEY);

    /* creating checkout list items */
    let subscriptionIncluded = false;
    const listPriceIDs = [];
    const listProductIDs = [];
    let stripeAccountID = null;
    let isLemonadeProduct = false;

    try {
      cartItems.forEach((item) => {
        /* check for lemonade product */
        if (item.isLemonadeProduct) {
          isLemonadeProduct = true;
        }
        /* check for stripeAccountID */
        if (item.stripeAccountID) {
          if (!stripeAccountID) {
            stripeAccountID = item.stripeAccountID;
          } else if (stripeAccountID !== item.stripeAccountID) {
            throw new Error("You can't buy products from different coaches at the same time.");
          }

          if (isLemonadeProduct) {
            throw new Error(
              "You can't buy lemonade products at the same time with other products."
            );
          }
        }

        listPriceIDs.push({ price: item.priceID, quantity: 1 });
        listProductIDs.push(item.programUID);
        // Check if subscription (recurring membership) or one-time payment (visit card)
        if (item.pricing.type === PRICING.TYPE.subscription) {
          subscriptionIncluded = true;
        }
      });
    } catch (err) {
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        title: "Checkout ERROR",
        message: err.message,
      };
      dispatch(notifyActions.sendNotification(notification));
      dispatch(cartActions.processingCheckout(false));
      return { ok: false, consentReason: null, gymTermsRefusal: null };
    }

    // call create checkout session in backend
    try {
      const checkoutSession = await createStripeCheckoutSession({
        mode: subscriptionIncluded ? "subscription" : "payment",
        line_items: listPriceIDs,
        success_url: `${window.location.origin}/payment-status?status=success`,
        // `from` lets the website's status page offer "try again" on the page the buyer left
        cancel_url: `${window.location.origin}/payment-status?status=fail&from=${encodeURIComponent(
          window.location.pathname
        )}`,
        stripeAccountID: stripeAccountID,
        isLemonadeProduct: isLemonadeProduct,
        /* Omitted entirely rather than sent as null: the server reads this key
         * only for a cart it has itself identified as holding a commitment, and
         * an empty one on every ordinary purchase would be noise on the wire
         * that a future stricter validator could reasonably reject. */
        ...(commitmentConsent ? { commitmentConsent } : {}),
        ...(gymTermsConsent ? { gymTermsConsent } : {}),
      });

      if (checkoutSession.data.url) {
        window.location.replace(checkoutSession.data.url);
      }
      return { ok: true, consentReason: null, gymTermsRefusal: null };
    } catch (err) {
      const consentReason = consentReasonOf(err);
      const gymTermsRefusal = gymTermsRefusalOf(err);
      const recoverable = Boolean(consentReason || gymTermsRefusal);
      /* A consent or gym-terms refusal is recoverable on the screen the buyer is already
       * looking at — re-fetch the terms, re-ask, carry on — so for a caller
       * that DECLARED it handles these, the refusal is handed back rather
       * than announced: a banner would talk over (or contradict) the answer
       * the recovery UI is about to give. For every other caller, staying
       * quiet would turn a refusal into a spinner that stops with no
       * explanation at all, so the banner fires as it always has. */
      if (!recoverable || !handlesConsentRefusals) {
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.error,
            title: "Checkout ERROR",
            message: err.message,
          })
        );
      }
      dispatch(cartActions.processingCheckout(false));
      return { ok: false, consentReason, gymTermsRefusal };
    }
  };
};
